import React, { useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

type ScenarioRow = {
  id: number;
  scenario_code: string;
  scenario_name: string;
  description: string;
  statut: string;
  is_exceptionnelle: number;
  created_at: string;
  updated_at: string;
  status_changed_at?: string | null;
  service_nom?: string;
  created_by_username?: string | null;
  validated_by_username?: string | null;
  can_edit?: boolean;
  can_validate_actif?: boolean;
};

const PanelCtaV1: React.FC = () => {
  const [rows, setRows] = useState<ScenarioRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editInterventionId, setEditInterventionId] = useState<number | null>(null);

  const loadRows = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const response = await fetch(`${getApiBaseUrl(true)}/panel/v1/cta/scenarios?page=1&limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement CTA');
      setRows(Array.isArray(data.data) ? data.data : []);
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const updateStatus = async (row: ScenarioRow, status: 'en_creation' | 'en_test' | 'actif') => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      setUpdatingId(row.id);
      const response = await fetch(`${getApiBaseUrl(true)}/panel/v1/cta/scenarios/${row.id}/status`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) throw new Error(data?.error || 'Erreur mise à jour statut');
      await loadRows();
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setUpdatingId(null);
    }
  };

  const openEditModal = (row: ScenarioRow) => {
    setError(null);
    setEditInterventionId(row.id);
  };

  const statusLabel = (status: string): string => {
    if (status === 'actif') return 'Actif';
    if (status === 'en_test') return 'En test';
    if (status === 'en_creation') return 'En création';
    return status;
  };

  const displayDate = (value?: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('fr-FR');
  };

  return (
    <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/75 p-4 backdrop-blur-sm">
      <h2 className="mb-4 text-sm font-semibold text-cockpit-text">Interventions CTA</h2>
      {loading && <p className="text-cockpit-text-2">Chargement...</p>}
      {error && <p className="text-red-300">{error}</p>}
      {!loading && !error && (
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm text-cockpit-text">
            <thead>
              <tr>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">ID Service</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Catégorie / Statut</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Except.</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Créé</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Modifié</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Validation</th>
                <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-[#1a2537]/80">
                  <td className="px-3 py-2">
                    <div className="font-semibold text-cockpit-text">{row.scenario_code}</div>
                    <div className="text-xs text-cockpit-text-2">{row.service_nom || '-'}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-cockpit-text">{row.scenario_name}</div>
                    <select
                      value={row.statut}
                      disabled={updatingId === row.id}
                      onChange={(event) => updateStatus(row, event.target.value as 'en_creation' | 'en_test' | 'actif')}
                      className="mt-1 w-36 rounded-lg border border-[#223046]/90 bg-[#0e1522]/75 px-2 py-1 text-xs text-cockpit-text backdrop-blur-sm disabled:opacity-50"
                    >
                      <option value="en_creation">{statusLabel('en_creation')}</option>
                      <option value="en_test">{statusLabel('en_test')}</option>
                      <option value="actif" disabled={!row.can_validate_actif}>
                        {statusLabel('actif')}
                      </option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-cockpit-text-2">{row.is_exceptionnelle ? 'Oui' : '—'}</td>
                  <td className="px-3 py-2">
                    <div className="text-cockpit-text">{displayDate(row.created_at)}</div>
                    <div className="text-xs text-cockpit-muted">{row.created_by_username || '—'}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-cockpit-text">{displayDate(row.updated_at)}</div>
                    <div className="text-xs text-cockpit-muted">{row.validated_by_username || '—'}</div>
                  </td>
                  <td className="px-3 py-2">
                    {row.statut === 'actif' ? (
                      <>
                        <div className="text-cockpit-text">{displayDate(row.status_changed_at)}</div>
                        <div className="text-xs text-cockpit-muted">{row.validated_by_username || '—'}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-cockpit-text-2">—</div>
                        <div className="text-xs text-cockpit-muted">—</div>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      <button
                        type="button"
                        onClick={() => openEditModal(row)}
                        className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-2 py-1 text-xs font-medium text-cockpit-text transition-colors hover:bg-cockpit-bg-2/45"
                      >
                        Modifier
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-emerald-500/40 bg-emerald-500/15 px-2 py-1 text-xs font-medium text-emerald-100 transition-colors hover:bg-emerald-500/25"
                      >
                        Tester
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-xs font-medium text-violet-100 transition-colors hover:bg-violet-500/25"
                      >
                        Historique
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editInterventionId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-cockpit-bg-2/70 p-3"
          role="dialog"
          aria-modal="true"
          onClick={() => setEditInterventionId(null)}
        >
          <div
            className="h-[92vh] w-[96vw] max-w-[1500px] overflow-hidden rounded-xl border border-[#223046]/90 bg-[#0e1522]/95 shadow-2xl backdrop-blur-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#1a2537] px-4 py-2">
              <h3 className="text-sm font-semibold text-cockpit-text">
                Modifier intervention #{editInterventionId}
              </h3>
              <button
                type="button"
                onClick={() => setEditInterventionId(null)}
                className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1 text-xs font-medium text-cockpit-text transition-colors hover:bg-cockpit-bg-2/45"
              >
                Fermer
              </button>
            </div>
            <iframe
              title={`Edition intervention ${editInterventionId}`}
              src={`/panel/interventions/edit/${editInterventionId}?embed=1`}
              className="h-[calc(92vh-42px)] w-full border-0"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelCtaV1;
